import { FormLabel } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAttachmentsService from '../hooks/useAttachmentsService'
import {
  AttachmentRespType,
  AttachmentType,
  CaseReportId,
  PartType,
} from '../models'
import { useStyles } from '../pages/styled'
import { AttachmentsService } from '../services/case-report/AttachmentsService'
import theme, { specialTheme } from '../theme'
import { MAX_FILE_NAME_LENGTH, prepareAttachmentFileForUpload } from '../utils/prepareAttachmentFileForUpload'
import { uploadAttachmentsSequentially } from '../utils/uploadAttachmentsSequentially'
import UploadButton from './UploadButton'

// file size limit 10 MB
// there is max-request-size 12MB set on the backend
const FILE_SIZE = 10 * 1024 * 1024

// Attachments je komponenta reprezentujici
// - seznam přiložených dokumentů ('attachments')
// - určitého typu ('attachmentType')
// - které lze nahrávat a mazat z formulářů CaseReport na určitém místě ('part')
//
// V API modelu tato komponenta reprezentuje typ `AttachmentRespType[]`.
//
// Vstupni `params` do komponenty AttachmentList je:
// A) Informace o tom, dokumenty jakeho typu se v danem miste prikladaji
// B) Jake dokumenty daného typu jsou již přiloženy
// C) Funkce informující o změně stavu (popř. vícero funkcí)
// X) Texty labelů ve view
//
export type AttachmentsProps = {
  // 0. caseReportId ("To which caseReport we shall add attachments to?")
  caseReportId: CaseReportId
  // A. Jakeho druhu jsou přiložené dokumenty == AttachmentTypeType
  attachmentType: AttachmentType
  // B. Kde na stránkách COL-PL se necházíme? (== K čemu se attachment přikládá)
  part: PartType
  // B2. ItemOrderNum - pod jakou item jsme? Nedefinovat, ci nastavit na null, pokud se nejedna o nejaky Item.
  itemOrderNum?: number | null
  // C. Jake dokumenty daného typu jsou již přiloženy
  attachments?: AttachmentRespType[]
  // D. Funkce informující o změně stavu seznamu příloh daného typu
  onAttachmentsChanged?: (event: AttachmentsChangedEvent) => void
  // E. OnBlur
  onBlur?: (event: any) => void
  //data: AttachmentListRespType | null
  labelText: string | any

  /**
   * The id of the `input` element.
   * Use this prop to make `label` and `helperText` accessible for screen readers.
   */
  id?: string
  /**
   * Name to be given to input element
   */
  name: string
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color?: 'primary' | 'secondary'
  /**
   * If `true`, the label should be displayed in a disabled state and the `input` element will be disabled.
   */
  disabled?: boolean
  /**
   * If `true`, the label should be displayed in an error state.
   */
  error?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  /**
   * If `true`, the label should use filled classes key.
   */
  labelFilled?: boolean
  /**
   * If `true`, the input of this label is focused (used by `FormGroup` components).
   */
  focused?: boolean
  /**
   * If `true`, the label will indicate that the input is required.
   */
  required?: boolean
}

export type AttachmentsChangedEvent = {
  value: {
    attachmentType: AttachmentType
    part: PartType
    attachments: AttachmentRespType[]
  }
}

const allowedExtensions = [
  'jpg',
  'png',
  'bmp',
  'doc',
  'docx',
  'gif',
  'jpeg',
  'heic',
  'pdf',
  'txt',
  'xls',
  'xlsx',
]

/**
 * Component representing a list of Attachments that can be uploaded to the server,
 * or removed from the server.
 *
 * @param props
 * @returns
 */
export const Attachments = (props: AttachmentsProps) => {
  const attachmentsService: AttachmentsService = useAttachmentsService()

  return (
    /* eslint-disable-next-line react/jsx-pascal-case */
    <Attachments_internal attachmentsService={attachmentsService} {...props} />
  )
}

type AttachmentsProps_internal = AttachmentsProps & {
  attachmentsService: AttachmentsService
}

/**
 * Component representing a list of Attachments that can be uploaded to the server,
 * or removed from the server.
 *
 * @param props
 * @returns
 */
const Attachments_internal = (props: AttachmentsProps_internal) => {
  // Functions that perform operations on remote
  const attachmentsService: AttachmentsService = props.attachmentsService

  // Component state, etc.
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const [uploadingInProgress, setUploadingInProgress] = useState(false)
  const [uploadedAttachments, setUploadedAttachments_internal] = useState(
    props.attachments ?? []
  )
  // console.log(props.attachments, 'props-attachments')
  useEffect(() => {
    // initialize list of uploadedAttachments to props.attachments on props.attachments changed
    //setUploadedAttachments(props.attachments ?? [], /*fireEvent=*/ false)
    setUploadedAttachments_internal(props.attachments ?? [])
  }, [props.attachments, setUploadedAttachments_internal])

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<
    React.ReactNode | null
  >(null)
  const [deletingInProgress_indices, setDeletingInProgress_indices] = useState<
    number[]
  >([])

  function canPerformUpload(): boolean {
    if (props.disabled) {
      return false
    }
    return !uploadingInProgress && !isDeletingInProgress()
  }

  function handleAttachmentFileSelectedForUpload(event: any): void {
    void handleAttachmentFileSelectedForUploadAsync(event)
  }

  async function handleAttachmentFileSelectedForUploadAsync(
    event: any
  ): Promise<void> {
    //alert('AttachmentFileSelectedForUpload: ' + event)
    if (!canPerformUpload()) {
      // cannot upload another attachment while upload is in progress
      alert(
        'AttachmentFileSelectedForUpload: ' +
        'cannot upload another attachment while upload is in progress'
      )
      return
    }

    const input = event.currentTarget as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (files.length === 0) {
      alert('No files selected for upload!')
      return
    }

    setUploadingInProgress(true)
    setUploadFileErrorMessage(null)

    let newAttachments = uploadedAttachments
    let failedFiles: { fileName: string; errorMessage: string }[] = []
    try {
      failedFiles = await uploadAttachmentsSequentially(files, {
        prepareFile: async (file) => {
          if (!isFileExtensionAllowed(file)) {
            return { file: null, errorMessage: t('uploadFile.err2') }
          }

          if (!isFileSizeAllowed(file)) {
            return { file: null, errorMessage: t('uploadFile.err3') }
          }

          if (file.name.length > MAX_FILE_NAME_LENGTH) {
            return { file: null, errorMessage: t('uploadFile.nameTooLong') }
          }

          return prepareAttachmentFileForUpload(file, {
            maxFileSizeInBytes: FILE_SIZE,
            t,
          })
        },
        uploadFile: performUploadAndHandleState,
        onUploaded: (uploadedAttachment) => {
          newAttachments = [...newAttachments, uploadedAttachment]
          setUploadedAttachments(newAttachments, /*fireChangedEvent=*/ true)
        },
        uploadErrorMessage: getAttachmentUploadErrorMessage,
        fallbackPreparationErrorMessage: t('uploadFile.uploadError'),
      })
    } finally {
      setUploadingInProgress(false)
      input.value = ''
    }

    if (failedFiles.length > 0) {
      setUploadFileErrorMessage(
        <>
          {t('uploadFile.batchError')}
          {failedFiles.map(({ fileName, errorMessage }, index) => (
            <React.Fragment key={`${fileName}-${index}`}>
              <br />
              {fileName}: {errorMessage}
            </React.Fragment>
          ))}
        </>
      )
    }
  }

  async function performUploadAndHandleState(
    file: File
  ): Promise<AttachmentRespType> {
    // get orderNum
    //const attachmentOrderNum: number = getNextAttachmentOrderNum()

    return attachmentsService.uploadAttachment({
      caseReportId: props.caseReportId,
      partType: props.part,
      attachmentType: props.attachmentType,
      file: file,
      itemOrderNum: props.itemOrderNum,
    })
  }

  function getAttachmentUploadErrorMessage(reason: any): string {
    // XXX zde bychom meli dostat jiz predzpracovany error ze strany nasi service
    // s informaci o typu chyby, ap.
    // v tomto miste pak reagujeme na danou chybu vhodnou hlaskou
    // (ne vsechny chyby jsou stejne a maji stejnou hlasku, ze ? ;)
    return 'ERROR: ' + reason
  }

  /** Sets state property 'uploadedAttachments' and fires value changed event if requested */
  function setUploadedAttachments(
    values: AttachmentRespType[],
    fireEvent: boolean
  ): void {
    if (!fireEvent) {
      setUploadedAttachments_internal(values)
    } else {
      setUploadedAttachments_internal(values)
      fireAttachmentsChangedEvt(values)
    }
  }

  // XXX chtel bych, abych mohl zmenit hodnotu ve state a pak ji pouzit pro vystreleni udalosti
  // nelze to tak, jelikoz hodnota ve state se jeste nezmenila (meni se asynchronne az pri render)
  // prozatim si budu hodnotu posilat v parametru teto metody.
  // pote bych ale mel 'uploadedAttachments' brat ze state -- a chytat se na toto pomoci useEffect
  function fireAttachmentsChangedEvt(
    uploadedAttachments: AttachmentRespType[]
  ) {
    if (!props.onAttachmentsChanged) {
      // do nothing if event handler not configured
      return
    }
    // fire event
    props.onAttachmentsChanged({
      value: {
        attachmentType: props.attachmentType,
        part: props.part,
        attachments: uploadedAttachments, // seznam uploadedAttachments ze state
      },
    })
  }

  function isFileSizeAllowed(file: File): boolean {
    return file.size <= FILE_SIZE
  }

  function isFileExtensionAllowed(file: File): boolean {
    const fileName = file.name
    const fileExtension = fileName.split('.').pop()?.toLowerCase()
    if (fileExtension == null) {
      return false
    } else {
      const isInAllowedExtensions = allowedExtensions.includes(fileExtension)
      return isInAllowedExtensions
    }
  }

  /* Delete attachment: modifies view state and deletes the attachment */
  const canPerformAnyDelete = canPerformUpload

  function isDeletingInProgress(): boolean {
    return deletingInProgress_indices.length > 0
  }

  function isItemDeleteInProgress(attachmentIndex: number): boolean {
    return deletingInProgress_indices.includes(attachmentIndex)
  }

  function performDeleteAttachment(
    item: AttachmentRespType,
    attachmentIndex: number
  ) {
    if (!canPerformAnyDelete()) {
      alert('Cannot delete attachments now. Programming error')
      return
    }
    if (isItemDeleteInProgress(attachmentIndex)) {
      alert(
        'Cannot delete attachment whose deleting is in progress. Programming error'
      )
      return
    }

    // Updates state before deleting starts
    setUploadFileErrorMessage(null)
    setDeletingInProgress_indices([
      ...deletingInProgress_indices,
      attachmentIndex,
    ])

    attachmentsService
      .deleteAttachment({
        caseReportId: props.caseReportId,
        attachmentId: item.attachmentId,
      })
      .then(() => {
        // Attachment deleted OK
        // 1. update component state:
        // a) attachment delete is no longer in progress
        // Array indexu polozek, ktere se prave mazou: 'deletingInProgress_indices'
        removeAttachmentFromDeletingInProgress(attachmentIndex)
        // Set error message to null == no error
        setUploadFileErrorMessage(null)

        // Array 'uploadedAttachments', ktere reprezentuje, co se zobrazuje jako nahrane attachmenty
        // + fire event that data was changed
        removeAttachmentFromUploadedAttachments(
          attachmentIndex,
          /*fireEvent=*/ true
        )
      })
      .catch((reason: any) => {
        // Attachment deleting error
        // 1. update component state:
        // a) attachment delete is no longer in progress
        removeAttachmentFromDeletingInProgress(attachmentIndex)
        // 2. show error message
        // XXX zde bychom meli dostat jiz predzpracovany error ze strany nasi service
        // s informaci o typu chyby, ap.
        // v tomto miste pak reagujeme na danou chybu vhodnou hlaskou
        // (ne vsechny chyby jsou stejne a maji stejnou hlasku, ze ? ;)
        setUploadFileErrorMessage('ERROR: ' + reason)
      })
  }

  function removeAttachmentFromDeletingInProgress(index: number) {
    // Array indexu polozek, ktere se prave mazou: 'deletingInProgress_indices'
    // impl: smazu jednu polozku z array a ulozim nove array
    setDeletingInProgress_indices(
      deletingInProgress_indices.filter((value: number) => {
        return value !== index
      })
    )
  }

  /**
   * Modifikuje Array nahranych attachmentu ve state: 'uploadedAttachments';
   * Z pole smažu položku na indexu 'attachmentIndex'
   */
  function removeAttachmentFromUploadedAttachments(
    attachmentIndex: number,
    fireEvent: boolean
  ) {
    const newUploadedAttachments: AttachmentRespType[] =
      uploadedAttachments.filter((value: AttachmentRespType, index: number) => {
        return index !== attachmentIndex
      })
    setUploadedAttachments(newUploadedAttachments, fireEvent)
  }

  return (
    <Grid item xs={12} sm={12} xl={12} container className={classes.gridItem}>
      <FormLabel
        // idealni by bylo predat labelu veskere konfiguracni property, co muze mit, ale to neumim nyni
        required={props.required}
        className={classes.labelArray}
        error={props.error}
      >
        {props.labelText}
      </FormLabel>
      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
        <Grid item xl={6} xs={6} sm={6} className={classes.fieldSize}>
          <UploadButton
            name={props.name}
            onChange={handleAttachmentFileSelectedForUpload}
            loading={uploadingInProgress}
            disabled={props.disabled}
          >
            {t('uploadFile.upload')}
          </UploadButton>
          {uploadFileErrorMessage /* Display error box only if there is error message */ && (
            <Grid item xl={12} xs={12} sm={12} className={classes.row}>
              <div className={classes.checkError}>{uploadFileErrorMessage}</div>
            </Grid>
          )}
        </Grid>
        <Grid item xl={6} xs={6} sm={6} className={classes.fieldBorder}>
          {uploadedAttachments &&
            uploadedAttachments?.length > 0 &&
            uploadedAttachments?.map((item, index) => (
              <UploadedAttachment
                item={item}
                key={index}
                deleteInProgress={isItemDeleteInProgress(index)}
                deleteDisabled={!canPerformAnyDelete()}
                deleteFunction={() => performDeleteAttachment(item, index)}
              />
            ))}
        </Grid>
      </Grid>
      {/* ErrorMessage */}
      <div className={classes.checkError}>{props.helperText}</div>
    </Grid>
  )
}

// Komponenta UploadedAttachment - součást seznamu nahraných attachmentů
const UploadedAttachment = (props: {
  item: AttachmentRespType
  //key: any - formik sends this but we dont declare it ourselves (TRO: don't know why)
  deleteInProgress: boolean
  deleteDisabled: boolean
  deleteFunction?: () => void
}) => {
  const classes = useStyles(theme)

  function canDelete(): boolean {
    return props.deleteFunction != null
  }

  function deleteItem(): void {
    if (props.deleteDisabled) {
      return
    }
    if (props.deleteFunction == null) {
      return
    }
    props.deleteFunction()
  }

  return (
    <div className={classes.rowUnderline}>
      <div>{props.item?.fileName}</div>
      <div>
        {props.deleteInProgress ? (
          <div color={specialTheme.colors.darkRed}>
            <CircularProgress
              // @ts-ignore
              color="inherit"
              size="1.2rem"
            />
          </div>
        ) : (
          canDelete() && (
            <div onClick={deleteItem}>
              <DeleteIcon />
            </div>
          )
        )}
      </div>
    </div>
  )
}
